import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { fetchManualTimeJobs, fetchTimeJobs, type TimeJob, type TimeJobListKind } from '@/data/scheduleTimeTracking';
import { safeErrorMessage } from '@/utils/appErrors';

/** Which search lists the jobs: a manager's (every job at the location, completed included) or clock-in's. */
export type TimeJobListSource = 'manual' | 'clock';
export type TimeJobListChoice = { job: TimeJob; kindLabel: string };

const KINDS: Array<{ kind: TimeJobListKind; label: string }> = [
  { kind: 'installation', label: 'Installation' }, { kind: 'service', label: 'External service' }, { kind: 'asset_service', label: 'Asset service' },
];

function useJobList(source: TimeJobListSource, locationId: string, authorizationKey: string, kind: TimeJobListKind, search: string, enabled: boolean) {
  return useInfiniteQuery({
    // The manual key predates the clock-in source; keep it so Add time entry's cache is unchanged.
    queryKey: source === 'manual' ? ['schedule-time-manual-jobs', authorizationKey, locationId, kind, search]
      : ['schedule-time-clock-job-list', authorizationKey, locationId, kind, search],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => source === 'manual' ? fetchManualTimeJobs(locationId, kind, search, pageParam) : fetchTimeJobs(locationId, search, pageParam, kind),
    getNextPageParam: page => page.has_more ? page.jobs[page.jobs.length - 1]?.id : undefined,
    placeholderData: keepPreviousData, enabled,
  });
}

/** Every kind of job in one list; rows keep load order (each kind's first page, then each kind's next), so Load more adds at the bottom. */
export function useTimeJobLists({ source, locationId, authorizationKey, search, enabled }: {
  source: TimeJobListSource; locationId: string; authorizationKey: string; search: string; enabled: boolean;
}) {
  const installations = useJobList(source, locationId, authorizationKey, 'installation', search, enabled);
  const services = useJobList(source, locationId, authorizationKey, 'service', search, enabled);
  const assetServices = useJobList(source, locationId, authorizationKey, 'asset_service', search, enabled);
  const lists = [installations, services, assetServices].map((query, index) => ({ query, label: KINDS[index].label }));
  const pageCount = Math.max(0, ...lists.map(list => list.query.data?.pages.length ?? 0));
  const choices: TimeJobListChoice[] = Array.from({ length: pageCount }, (_, index) => index).flatMap(index => lists.flatMap(list =>
    (list.query.data?.pages[index]?.jobs ?? []).map(job => ({ job, kindLabel: list.label }))));
  const failed = lists.filter(list => list.query.isError);
  return {
    choices,
    loading: lists.some(list => list.query.isPending || list.query.isPlaceholderData),
    error: failed.length ? safeErrorMessage(failed[0].query.error, 'Jobs could not be loaded.') : undefined,
    retry: () => { failed.forEach(list => { void list.query.refetch(); }); },
    hasMore: lists.some(list => list.query.hasNextPage),
    loadingMore: lists.some(list => list.query.isFetchingNextPage),
    loadMore: () => { lists.forEach(list => { if (list.query.hasNextPage) void list.query.fetchNextPage(); }); },
  };
}
